import { useCallback, useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { loadOpenStatements } from "@/billing/services/open-statements";
import { OpenStatementsFilters, OpenStatementsRow } from "@/billing/types/open-statements";

export interface UseOpenStatementsTableDataResult {
  rows: OpenStatementsRow[];
  isLoading: boolean;
  error: Error | undefined;
}

/**
 * Loads the practice's open statements. Returns no rows and no error while `practiceId` is
 * undefined, so the caller can render before the route param resolves. Load failures go to Sentry;
 * the caller shows the message.
 *
 * `filters` are applied after loading the practice statements, so changing them reloads. The status
 * list is depended on by value — a new array with the same statuses does not refetch.
 */
export function useOpenStatementsTableData(
  practiceId: string | undefined,
  refreshKey = 0,
  filters?: OpenStatementsFilters,
): UseOpenStatementsTableDataResult {
  const medplum = useMedplum();
  const [rows, setRows] = useState<OpenStatementsRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  // Only the newest load may write state, so a refresh that resolves after a later one does not
  // replace its rows with stale ones.
  const latestRequestIdRef = useRef(0);
  // `load` depends on the statuses by value, so a caller that rebuilds the filters object each
  // render does not retrigger the fetch on every render.
  const statuses = filters?.statuses ?? [];
  const statusKey = statuses.join(",");

  const load = useCallback(async () => {
    if (!practiceId) {
      latestRequestIdRef.current += 1;
      setRows([]);
      setError(undefined);
      setIsLoading(false);
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setIsLoading(true);
    setError(undefined);

    const result = await loadOpenStatements(medplum, practiceId, { statuses });
    if (requestId !== latestRequestIdRef.current) return;

    if (result.ok) {
      setRows(result.data);
    } else {
      const loadError = new Error(`Open Statements load failed: ${result.reason}`);
      setRows([]);
      setError(loadError);
      Sentry.captureException(loadError, { extra: { details: result.details } });
    }
    setIsLoading(false);
    // `statuses` is depended on by value through `statusKey`, so a caller that rebuilds the filters
    // object each render does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medplum, practiceId, statusKey]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return { rows, isLoading, error };
}
